import type { InferContractRouterOutputs } from '@orpc/contract'
import type { BaseContract } from 'pixai-contract/api/orpc'
import {
  ImageVariant,
  type ArtworkPreviewFragment,
  type ArtworkType,
  type Media,
  type MediaType,
  type User,
} from 'src/services/apollo/generated/graphql'
import type {
  ArtworkGetDailyRankingArtworksResponse,
  ArtworkGetFollowingNewsArtworksResponse,
  ArtworkGetRecommendArtworksResponse,
  ArtworkGetSimilarArtworksResponse,
  ArtworkGetTrendingArtworksResponse,
  TackCuratedArtworksResponse,
} from '../generated/types.gen'

type GenerationModelImportExternalModelResponse =
  InferContractRouterOutputs<BaseContract>['generationModel']['importExternalModel']

type OrpcArtworkItem = ArtworkGetRecommendArtworksResponse['data'][number]
type OrpcAuthor = NonNullable<OrpcArtworkItem['author']>
type OrpcArtworkDebugInfo = NonNullable<OrpcArtworkItem['debugInfo']>

type UserArtworkItem =
  InferContractRouterOutputs<BaseContract>['user']['getUserArtworksLatest']['data'][number]

// Items returned from feeds whose item schema is `artworkListItemSchema`.
// All such items now carry `debugInfo` (admin-only overlay); only
// `feedMetadata` remains recommend-specific.
type OrpcTackArtworkItem =
  | TackCuratedArtworksResponse['data'][number]
  | ArtworkGetTrendingArtworksResponse['data'][number]
  | ArtworkGetDailyRankingArtworksResponse['data'][number]
  | ArtworkGetFollowingNewsArtworksResponse['data'][number]
  | ArtworkGetSimilarArtworksResponse['data'][number]
  | UserArtworkItem

type OrpcArtworkInput = OrpcTackArtworkItem &
  Partial<Pick<OrpcArtworkItem, 'feedMetadata'>>

type OrpcMedia = NonNullable<
  GenerationModelImportExternalModelResponse['mediaList']
>[number]

/**
 * ArtworkPreviewFragment with oRPC-only fields that aren't part of the GraphQL
 * shape but travel with recommend/similar responses for later hydration.
 * `tackIds` is always present (possibly empty), mirroring the server contract.
 * `debugInfo` is forwarded so card components can render the admin debug HUD
 * without re-reading the JSON payload — populated only for admin viewers.
 */
export type OrpcArtworkPreviewFragment = ArtworkPreviewFragment & {
  tackIds: string[]
  debugInfo?: OrpcArtworkDebugInfo | null
}

/**
 * Map an oRPC artwork item to a partial GraphQL ArtworkPreviewFragment type
 * so existing card components can render it without changes.
 */
export function toArtworkPreviewFragment(
  item: OrpcArtworkInput,
): OrpcArtworkPreviewFragment {
  return {
    __typename: 'Artwork',
    id: item.id,
    mediaId: item.mediaId,
    title: item.title ?? '',
    authorId: item.authorId ?? undefined,
    prompts: item.prompts,
    hidePrompts: item.hidePrompts,
    visibility: item.visibility,
    isSensitive: item.isSensitive,
    isNsfw: item.isSensitive,
    type: (item.type ?? undefined) as ArtworkType,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    likedCount: item.likedCount,
    liked: item.liked,
    commentCount: item.commentCount,
    paidCredit: item.debugInfo?.paidCredit ?? undefined,
    aesScore: item.debugInfo?.aesScore ?? undefined,
    videoMediaId: item.videoMediaId ?? undefined,
    // Derive the partial `ModerationFlag` shape from the `moderation` array
    // (the server's `flag` field is deprecated). Optional-chain guards against
    // a deploy gap where an old server lacks `moderation`.
    flag: item.moderation
      ? {
          __typename: 'ModerationFlag',
          isSensitive: item.moderation.includes('sensitive'),
          isMinors: false,
          shouldBlur: item.moderation.includes('blurred'),
        }
      : undefined,
    extra: {
      imageBlurHash: item.imageBlurHash,
      faceCenter: item.faceCenter,
    },
    feedMetadata: item.feedMetadata
      ? { __typename: 'FeedMetadata', ...item.feedMetadata }
      : undefined,
    // TODO: media is not a complete Media entity.
    media: item.media ? mapOrpcArtworkListMediaToMedia(item.media) : undefined,
    // TODO: author is not a complete User entity.
    author: (item.author ? mapAuthor(item.author) : undefined) as
      | User
      | undefined,
    tackIds: item.tackIds ?? [],
    debugInfo: item.debugInfo ?? undefined,
  } satisfies OrpcArtworkPreviewFragment
}

function mapAuthor(author: OrpcAuthor) {
  return {
    id: author.id,
    username: author.username,
    displayName: author.displayName,
    avatarMediaId: author.avatarMediaId,
    avatarSmallThumbnailMediaUrl: author.avatarUrl ?? undefined,
    membership:
      author.membership != null ? { tier: author.membership.tier } : undefined,
    activeDecorations: author.activeDecorations.map(d => ({
      decorationId: d.decorationId,
      userRemark: d.userRemark,
      decoration: {
        id: d.decoration.id,
        code: d.decoration.code,
        type: d.decoration.type,
        data: d.decoration.data,
      },
    })),
    // Forwarded from the v2 artwork response. Consumers hydrate these via
    // `useAuthorActiveDecorations` (see `src/services/orpc/decoration.ts`)
    // so the inline `activeDecorations` payload above can be dropped
    // server-side once all UI sites have migrated.
    activeDecorationIds: author.activeDecorationIds,
  }
}

// TODO: Consider merging with mapOrpcArtworkListMediaToMedia
export function mapOrpcMediaToMedia(
  media?: OrpcMedia | null,
): Media | undefined {
  if (!media) return undefined
  return {
    id: media.id,
    type: media.type as MediaType,
    width: media.width ?? undefined,
    height: media.height ?? undefined,
    imageType: media.imageType ?? undefined,
    fileUrl: media.fileUrl ?? undefined,
    duration: media.duration ?? undefined,
    thumbnailUrl: media.thumbnailUrl ?? undefined,
    hlsUrl: media.hlsUrl ?? undefined,
    size: media.size ?? undefined,
    urls:
      media.urls?.map(u => ({
        url: u.url ?? undefined,
        variant: u.variant ?? undefined,
      })) ?? undefined,
  }
}

function mapOrpcArtworkListMediaToMedia(
  media: UserArtworkItem['media'],
): Media | undefined {
  if (!media) return undefined
  return {
    width: media.width,
    height: media.height,
    thumbnailUrl: media.thumbnailUrl ?? undefined,
    urls: [
      ...(media.thumbnailUrl
        ? [{ variant: ImageVariant.Thumbnail, url: media.thumbnailUrl }]
        : []),
      ...(media.publicUrl
        ? [{ variant: ImageVariant.Public, url: media.publicUrl }]
        : []),
    ],
  } as Media
}
